import React, { useEffect, useMemo, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { useAuth } from './AuthContext';
import './LoginPage.css';

export function ResetPasswordPage() {
    const history = useHistory();
    const { user, loading, updatePassword, signOut } = useAuth();
    const recoveryError = useMemo(() => {
        const params = new URLSearchParams(window.location.search);
        const queryError = params.get('error_description') || params.get('error');

        if (queryError) return queryError;

        if (window.location.hash && window.location.hash.length > 1) {
            const hashParams = new URLSearchParams(window.location.hash.slice(1));
            return hashParams.get('error_description') || hashParams.get('error') || '';
        }

        return '';
    }, []);
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState('');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!recoveryError) return undefined;

        let active = true;

        const clearSessionAndRedirect = async () => {
            await signOut();
            if (!active) return;
            history.replace(`/forgot-password?recoveryError=${encodeURIComponent(recoveryError)}`);
        };

        clearSessionAndRedirect();

        return () => {
            active = false;
        };
    }, [history, recoveryError, signOut]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (password.length < 6) {
            setError('Password must be at least 6 characters.');
            return;
        }

        if (password !== confirmPassword) {
            setError('Passwords do not match.');
            return;
        }

        setSaving(true);
        const updateError = await updatePassword(password);
        if (updateError) {
            setError(updateError);
            setSaving(false);
            return;
        }

        await signOut();
        history.replace('/login?passwordReset=success');
    };

    return (
        <section className="login-page-wrap">
            <div className="login-card">
                <div className="login-brand-row">
                    <div className="login-brand-mark" aria-hidden="true">R</div>
                    <h1 className="login-brand-title">regattamanager</h1>
                </div>

                <p className="login-subtitle">Choose a new password</p>

                {recoveryError ? (
                    <p className="login-secondary-note mb-0">Redirecting you to request a new recovery link...</p>
                ) : loading ? (
                    <p className="login-secondary-note mb-0">Validating your recovery link...</p>
                ) : !user ? (
                    <>
                        <div className="alert alert-warning login-alert mb-3">
                            This recovery link is invalid or has expired.
                        </div>
                        <p className="login-secondary-note">
                            Request a new password reset email to continue.
                        </p>
                        <p className="login-footer-note">
                            <Link to="/forgot-password">Request another reset link</Link>
                        </p>
                    </>
                ) : (
                    <>
                        {error && <div className="alert alert-danger login-alert">{error}</div>}

                        <p className="login-secondary-note">
                            Email re-validated for <strong>{user.email}</strong>. Set your new password below.
                        </p>

                        <form onSubmit={handleSubmit} className="login-form">
                            <div className="mb-3">
                                <label className="form-label" htmlFor="reset-password">New password</label>
                                <input
                                    id="reset-password"
                                    type="password"
                                    className="form-control"
                                    value={password}
                                    onChange={e => setPassword(e.target.value)}
                                    autoComplete="new-password"
                                    required
                                />
                            </div>
                            <div className="mb-3">
                                <label className="form-label" htmlFor="reset-password-confirm">Confirm new password</label>
                                <input
                                    id="reset-password-confirm"
                                    type="password"
                                    className="form-control"
                                    value={confirmPassword}
                                    onChange={e => setConfirmPassword(e.target.value)}
                                    autoComplete="new-password"
                                    required
                                />
                            </div>
                            <button type="submit" className="btn btn-primary w-100 login-submit-btn" disabled={saving}>
                                {saving ? 'Updating password...' : 'Save new password'}
                            </button>
                        </form>
                    </>
                )}
            </div>
        </section>
    );
}